import Handlebars from "handlebars"
import { Controller } from "@hotwired/stimulus"
// any imports into this file need to be done manually. as importing the imports file causes recursion issuse

class ServerError extends Error{
    date : Date
    jres : object
    errorData : object

    constructor(jres) {
        var error = "none"
        if (jres["error"]) {
            error = jres["error"]
        } else if (jres["errors"]){ // form validation errors
            error = jres["errors"].join(", ")
        } else if (jres["warning"]) {
            error = jres["warning"]
        }
        super(error)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ServerError)
        }
        this.name = "ServerError"
        this.date = new Date()
        this.jres = jres
        this.errorData = jres["errorData"]
    }
}

export default class AppController extends Controller {

    lang = "en-NZ"
    locale = { style: 'currency', currency: 'NZD', minimumFractionDigits: 0, maximumFractionDigits: 0 }
    defaultImgUrl = "https://media.witglobal.net/stmedia/wuerth/images/std.lang.all/resolutions/normal/1250px/150006.jpg"
    isIOS = false
    static env : (string | null) = null
    static companyId : (number | null) = null

    constructor(context : any) {
        super(context)
        Object.assign(String.prototype, {
            toCurrency(precision=0) : String{
                var locale = { style: 'currency', currency: 'NZD', minimumFractionDigits: precision, maximumFractionDigits: precision }
                return parseFloat(this).toLocaleString(this.lang, locale)
            }
        })
        this.isIOS = /iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    }

    get env() : string | null {
        return AppController.env
    }

    get dev() : boolean {
        return AppController.env === "development"
    }

    get prod() : boolean {
        return AppController.env === "production"
    }

    get companyId() : number | null {
        return AppController.companyId
    }
    
    // bind window.orsyXP.flash functions to this so we can call this.flashWhatever in any controller
    flashInfo(message : string, title=null) : void {
        window.orsyXP.flash("info", message, title)
    }

    flashSuccess(message : string, title=null) : void {
        window.orsyXP.flash("success", message, title)
    }

    flashWarn(message : string, title=null) : void {
        window.orsyXP.flash("warn", message, title)
    }

    flashError(message : string, title=null) : void {
        console.error(message)
        window.orsyXP.flash("error", message, title)
    }

    flashClearAll() : void {
        window.orsyXP.flashClearAll()
    }

    closeAllDialogs(){
        return globalThis.orsyXP.closeAllDialogs()
    }

    prompt(message : string) : Promise<string>{
        return globalThis.orsyXP.prompt(message)
    }

    confirm(message : string, returnDeny = false) : Promise<boolean>{
        return globalThis.orsyXP.confirm(message, returnDeny)
    }

    alert(message : string) : Promise<any>{
        return globalThis.orsyXP.alert(message)
    }


    jsonGet(url : string, urlParams : Object = undefined, handleJsonErr = true, abortController = null) : Promise<any>{
        const urlWithParams = new URL(url, window.location.origin)
        if (urlParams != undefined){
            Object.entries(urlParams).forEach(entry => {
                const [key, value] = entry
                urlWithParams.searchParams.set(key, value)
            })
        }
        
        return this.jsonFetch(urlWithParams.toString(), "GET", undefined, handleJsonErr, abortController)

    }

    jsonPost(url : string, json : object = undefined, handleJsonErr = true, timeout = 90000, abortController = null) : Promise<any>{
        return this.jsonFetch(url, "POST", json, handleJsonErr, timeout, abortController)
    }

    jsonDelete(url : string, json : object = undefined, handleJsonErr = true, timeout = 90000, abortController = null) : Promise<any>{
        return this.jsonFetch(url, "DELETE", json, handleJsonErr, timeout, abortController)
    }

    jsonPatch(url : string, json : object = undefined, handleJsonErr = true, timeout = 90000, abortController = null) : Promise<any>{
        return this.jsonFetch(url, "PATCH", json, handleJsonErr, timeout, abortController)
    }

    
    jsonFetch(url : string, method : string, json : Object = undefined, handleJsonErr = true, timeout = 90000, abortController = null) : Promise<any>{
        return new Promise<Object>((resolve, reject) => {
            fetch(url, {method: method, body: JSON.stringify(json), headers: this.jsonHeaders(), signal: abortController == null ? AbortSignal.timeout(timeout) : abortController.signal})
            .then((res) => {
                if (res.status === 500){
                    this.flashError("500: Server Error. Check logs for details.")
                    return
                }
                try{
                    res.json().then(jres => {
                        if(this.fetchErrorHandler(jres, res.status, "Response not valid", handleJsonErr)){
                            resolve(jres)
                        }
                        reject(new ServerError(jres))
                    })
                }catch(err){
                    console.error(err)
                    this.flashError("Parser error, response from server was not valid.")
                }
            }).catch(err => {
                // ignore connection abort errors as these are triggered by our own code
                if (err.name !== 'AbortError') { 
                    this.flashError(err)
                }
            })
        })
    }

    formPost(url : string, formData : FormData | HTMLFormElement, handleJsonErr = true) : Promise<any> {
        if (formData instanceof HTMLFormElement){
            formData = new FormData(formData)
        }
        return new Promise<Object>((resolve, reject) => {
            fetch(url, {method: "POST", body: formData, headers: this.formHeaders()})
            .then((res) => {
                res.json().then(jres => {
                    if(this.fetchErrorHandler(jres, res.status, "Response not valid", handleJsonErr)){
                        resolve(jres)
                    }
                    reject(new ServerError(jres))
                })
            }).catch(err => {
                // ignore connection abort errors as these are triggered by our own code
                if (err.name !== 'AbortError') { 
                    this.flashError(err)
                }
            })
        })
    }


    fetchErrorHandler(res : JSON, status, genericMessage, handleJsonErr = true){
        try {
            // sometimes we want to pass through this error text anyways to display it somewhere on the page. but most of the time we just want to do the flash
            if (handleJsonErr){
                if (res["error"]) {
                    window.scrollTo(0, 0)
                    this.flashError(res["error"].toString(), "Server Error")
                    
                    return false
                } else if (res["errors"]){ // form validation errors
                    this.flashError(res["errors"].map((err) => err), "Error")
                    for(var err of res["errors"]){
                        console.error(err)
                    }
                    return false
                } else if (res["warning"]) {
                    window.scrollTo(0, 0)
                    this.flashWarn(res["warning"], "Error")
                    console.warn(res["warning"])
                    return false
                }
            }
            if (status == 500){
                window.scrollTo(0, 0)
                this.flashError(genericMessage, "Server Error")
                console.error(genericMessage)
                return false
            }else if (status === 422){
                console.error("Got 422 response code, likely invalid CSRF token.")
                this.flashError(`${status}. Please try reloading the page!`, `Bad Response Code`)
                return false
            }else{
                return true
            }
        } catch (e) {
            window.scrollTo(0, 0)
            this.flashError(`Status: ${status} Error: ${e}`, "Site Error")
            console.error(`Status: ${status}. Site error ${e}`)
            return false
        }
        return true
    }

     // compiles handlebar templates (via html template elms) and attaches them to this controller
    compileTemplates(...templateTargets : HTMLTemplateElement[]){
        templateTargets.forEach(template => {
            this.compileTemplate(template)
        })
    }

    // compiles handlebar templates and attaches them to this controller
    compileTemplate(template : HTMLTemplateElement){
        var varName = template.getAttribute(`data-${this.identifier}-target`)
        this[varName] = Handlebars.compile(template.innerHTML)
    }


    createCookie(name, value) {
        var expires = ""
        document.cookie = name + "=" + value + expires + "; path=/"
    }

    getCookie(name) {
        var nameEQ = name + "="
        var ca = document.cookie.split(';')
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i]
            while (c.charAt(0) == ' ') c = c.substring(1, c.length)
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length)
        }
        return null
    }

    eraseCookie(name) {
        this.createCookie(name, "")
    }

    decodeEntities(encodedString) {
        var translate_re = /&(nbsp|amp|quot|lt|gt);/g
        var translate = {
            "nbsp":" ",
            "amp" : "&",
            "quot": "\"",
            "lt"  : "<",
            "gt"  : ">"
        };
        return encodedString.replace(translate_re, function(match, entity) {
            return translate[entity]
        }).replace(/&#(\d+);/gi, function(match, numStr) {
            var num = parseInt(numStr, 10)
            return String.fromCharCode(num)
        });
    }

    getCSRF(){
        return document.querySelector('meta[name=csrf-token]').getAttribute("content")
    }

    isDarkMode(){
        return document.querySelector<HTMLElement>("body").className == "theme-dark"
    }

    static isDarkMode(){
        return document.querySelector<HTMLElement>("body").className == "theme-dark"
    }


    camelToNormal(string){
        return string.replace(/([A-Z])/g, ' $1').replace(/^./, function(str){ return str.toUpperCase() })
    }

    humanize(string){
        return string.replace(/([A-Z])/g, ' $1').replace(/^./, function(str){ return str.toUpperCase() })
    }

    uniqueArray(array : Array<any>) : Array<any>{
        return array.reduce((acc, value) => (
            acc.includes(value) ? acc : [...acc, value]
        ), [])
    }

    tryParseInt(input : any, fallback : number = 0, filter : RegExp = null) : number{
        if (filter !== null){
            input = input.replace(filter, '')
        }
        // yikes javascript, undefined and null do different things when passed into Number()
        if (input === null){ 
            return fallback
        }
        let number = Number(input)
        if (!Number.isFinite(number)){
            return fallback
        }
        return number
    }

    parseBool(input : any) : boolean{
        return String(input).toLowerCase() === "true"
    }

    uniqueDateArray(array : Array<Date>) : Array<Date>{
        var tmpSet = new Set(array.map((date) => date.getTime()))
        return Array.from(tmpSet).map((timestamp) => new Date(timestamp))
    }

    jsonHeaders(){
        return {"Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.getCSRF()}
    }

    formHeaders(){
        return {"Accept": "application/json", "X-CSRF-Token": this.getCSRF()}
    }

    copyToClipboard(copyText){
        if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(copyText).then(() => {
                this.flashSuccess(`Copied "${copyText}" to clipboard`)
            })
        }else{
            this.flashWarn("Cant copy text to clipboard")
        }
    }

    searchTable(controller : Controller, searchElm : HTMLInputElement, apiEndpoint : string, onResult, pageId=1){
            this.flashClearAll()
            var loadingContainer = document.querySelector<HTMLElement>(".loadingContainer")
            if (loadingContainer){
                loadingContainer.classList.remove("hidden")
            }
            
            fetch(`${apiEndpoint}?q=${searchElm.value}&pageId=${pageId}`, {method: "POST", headers: {"Content-Type": "application/json", "X-CSRF-Token": this.getCSRF()}})
            .then((e) => {
                if (e.status == 200){
                    e.json().then(jres => {
                        if (this.fetchErrorHandler(jres, e.status, "Failed to search")){
                            if (loadingContainer){
                                loadingContainer.classList.add("hidden")
                            }
                            onResult.bind(controller, jres)()
                        }
                    })
                }
            }).catch((e) => {
                this.flashError(e.message)
                throw e
            })
        }

    

}